import { useCallback, useEffect, useState } from 'react'
import type { EhsrClause } from '../../ehsr/types'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { EhsrSelect } from '../hazards/EhsrSelect'
import { dialogPortaledPopoverGuard } from '@/components/ui/dialog-portaled-popover-guard'

type AddEhsrsToRrmDialogProps = {
  isOpen: boolean
  onClose: () => void
  ehsrOptions: EhsrClause[]
  ehsrClauseIds: string[]
  onSave: (ehsrClauseIds: string[]) => void
  isSaving: boolean
}

export function AddEhsrsToRrmDialog({
  isOpen,
  onClose,
  ehsrOptions,
  ehsrClauseIds,
  onSave,
  isSaving,
}: AddEhsrsToRrmDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [selectedEhsrClauseIds, setSelectedEhsrClauseIds] =
    useState<string[]>(ehsrClauseIds)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setSelectedEhsrClauseIds(ehsrClauseIds)
  }, [isOpen, ehsrClauseIds])

  const handleClose = useCallback(() => {
    setSelectedEhsrClauseIds(ehsrClauseIds)
    onClose()
  }, [ehsrClauseIds, onClose])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSaving) {
        handleClose()
      }
    },
    [handleClose, isSaving]
  )

  const handleSave = () => {
    onSave(selectedEhsrClauseIds)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className='sm:max-w-lg'
        showCloseButton={!isSaving}
        {...dialogPortaledPopoverGuard}
        onEscapeKeyDown={(event) => {
          if (isSaving) {
            event.preventDefault()
          }
        }}
        onInteractOutside={(event) => {
          if (isSaving) {
            event.preventDefault()
            return
          }
          dialogPortaledPopoverGuard.onInteractOutside?.(event)
        }}
        onPointerDownOutside={(event) => {
          if (isSaving) {
            event.preventDefault()
            return
          }
          dialogPortaledPopoverGuard.onPointerDownOutside?.(event)
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t('riskReductionMeasuresLibraryPage.rrmDetail.addEhsrsDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-2'>
          <Label>{t('hazardsPage.hazardDetail.ehsr')}</Label>
          <EhsrSelect
            options={ehsrOptions}
            value={selectedEhsrClauseIds}
            onChange={setSelectedEhsrClauseIds}
            placeholder={t('hazardsPage.hazardDetail.ehsrPlaceholder')}
            disabled={isSaving}
          />
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {t('riskReductionMeasuresLibraryPage.formDialog.updateButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
